import * as React from 'react'

import NodeInputField from '../general/NodeInputField';
import { GraphWorker } from './algorithms/WebWorker';
import { Button } from '@mui/material';
import SatoshiInput from './SatoshiInput';
import { GraphDatasetManager } from '../graph/models/GraphDataset.interface';
import { GraphNode } from '../graph/models/GraphNode.interface';
import { useRouter } from 'next/router'
import Loader from "react-loader-spinner";
import { ShortestPathGraphContext } from './ShortestPathGraphProvider';
import ActionButton from '../general/ActionButton';
import { ISimpleNode } from '../node/nav/ISimpleNode';


const ShortestPathInput: React.FC = () => {
    const router = useRouter();
    const spGraphContext = React.useContext(ShortestPathGraphContext);
    const [source, setSource] = React.useState<GraphNode>(null);
    const [target, setTarget] = React.useState<GraphNode>(null);
    const [satoshis, setSatoshis] = React.useState<number>(10000);
    const [isCalculating, setIsCalculating] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>(null);
    const [autoStart, setAutostart] = React.useState<boolean>(false);


    const workerRef = React.useRef<GraphWorker>();

    function simpleToGraphNode(node: ISimpleNode): GraphNode {
        if (!node) {
            return null;
        }
        return spGraphContext.wholeDataset.nodes.filter(graphNode => graphNode.id === node.id)[0];
    }

    React.useEffect(() => {
        workerRef.current = new GraphWorker();

        return () => {
            workerRef.current.terminate()
        }
    }, []);

    React.useEffect(() => {
        setUrl();
        if (autoStart && target && source && satoshis) {
            setAutostart(false);
            calculate();
        }
        if (target && source && satoshis) {
            setError(null);
            return;
        }
        if (!target || !source || !satoshis) {
            spGraphContext.setResult(undefined);
        }
    }, [source, target, satoshis, autoStart]);

    React.useEffect(() => {
        if (!spGraphContext.wholeDataset){
            return;
        }
        const sourceId = router.query.source as string;
        const targetId = router.query.target as string;
        const sat = router.query.sat as string;

        if (sourceId) {
            const node = GraphDatasetManager.getNode(sourceId, spGraphContext.wholeDataset);
            if (node) {
                setSource(node);
            } else {
                console.error('Source node id not valid:', sourceId)
            }
        }
        if (targetId) {
            const node = GraphDatasetManager.getNode(targetId, spGraphContext.wholeDataset);
            if (node) {
                setTarget(node);
            } else {
                console.error('Target node id not valid:', targetId)
            }
        }
        if (sat) {
            setSatoshis(Number.parseInt(sat));
        }

        if (sat && sourceId && targetId) {
            setAutostart(true);
        }
    }, [spGraphContext.wholeDataset]);

    function setUrl() {
        if (!target || !source || !satoshis) {
            router.push({
                query: {}
              }, '/get-route', { shallow: true })
            return;
        }
        router.push({
            query: { 
                sat: satoshis,
                source: source.id,
                target: target.id
            }
          }, 
          '/get-route?sat=' + satoshis + '&source=' + source.id + '&target=' + target.id, 
          { shallow: true }
          )
    }

    async function calculate() {
        if (!target || !source || !satoshis) {
            setError('Source, target and satoshis are required to calculate the shortest path.')
            return;
        }
        setError(null);
        setIsCalculating(true);
        const path = await workerRef.current.shortestPath(source, target, spGraphContext.wholeDataset, satoshis);

        if (path.nodes.length <= 1) {
            setError('No path found.')
        } else {
            spGraphContext.setResult(path);
        }
        setIsCalculating(false);
    }

    return (
        <div className="">
            <p className="text-center pb-2">Use a pubkey or an alias to select a node.</p>
            <div className="flex flex-col md:flex-row justify-center items-center">
                <div className="md:mr-1 mt-2 w-64 md:inline-block">
                    <NodeInputField value={source} disabled={isCalculating} label="Source node" onChange={node => setSource(simpleToGraphNode(node))}></NodeInputField>
                </div>
                <div className="md:mr-1 mt-2 w-64 md:inline-block">
                    <NodeInputField value={target}  disabled={isCalculating} label="Target node" onChange={node => setTarget(simpleToGraphNode(node))}></NodeInputField>
                </div>
                <div className="md:mr-1 mt-2 w-64 md:inline-block">
                    <SatoshiInput value={satoshis} label="Satoshis" disabled={isCalculating}
                    onChange={satoshi => setSatoshis(satoshi)}></SatoshiInput>
                </div>
                <div className="mt-2 w-64 md:w-auto md:inline-block">
                    <ActionButton onClick={event => calculate()} style={{height: '56px'}} className="w-full md:h-[56px]" color="primary" variant="contained" 
                    size="large" disableElevation={true}
                    disabled={isCalculating}>
                        {isCalculating &&
                        <Loader color="#9ca3af" type="ThreeDots" width="40px"></Loader>
                        }
                        
                        {!isCalculating &&
                        <span>Find</span>
                        }
                         
                        </ActionButton>
                </div>
            </div>
            {error &&             
            <div className="flex justify-center">
                <p className="text-red-700">{error}</p>
            </div>}
        </div>
    );
}

export default ShortestPathInput;


